import React, { useEffect, useRef } from "react";
import { FiEdit3, FiSave, FiTrash, FiXCircle } from "react-icons/fi";

import styles from "../ChatHistory.module.scss";
import useOutsideClick from "hooks/useClickAway";

interface EditableChatHistoryElementProps {
  chatId: string;
  /**
   * The current name of the ChatThread being renamed/edited.
   */
  chatName: string;
  /**
   * Called when the user clicks outside the edit UI.
   * For example, cancel editing if the user clicks away to change Persona or
   * send a message.
   */
  onOutsideClick: () => void;
  /**
   * Called when the user clicks the save button.
   */
  onSave: (chatId: string, newChatName: string) => void;
  /**
   * Called when the user clicks the cancel button.
   */
  onCancel: () => void;
  /**
   * Called when the user clicks the delete button.
   */
  onDelete: (chatId: string) => void;
  /**
   * Reference to the <ul> element for dynamic positioning on scroll.
   */
  threadListRef: React.RefObject<HTMLUListElement | null>;
}

/**
 * Represents a chat in the chat history list that is currently being edited.
 */
export default function EditableChatHistoryElement({
  threadListRef,
  chatId,
  chatName,
  onOutsideClick,
  onSave,
  onCancel,
  onDelete,
}: EditableChatHistoryElementProps) {
  const menuRef = useOutsideClick(onOutsideClick);
  const editMenuRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const threadList = threadListRef.current;

    const adjustMenuPosition = () => {
      if (editMenuRef.current) {
        const menu = editMenuRef.current;
        const parent = menu.parentElement;
        if (parent) {
          const parentRect = parent.getBoundingClientRect();
          const menuRect = menu.getBoundingClientRect();

          const viewportHeight = window.innerHeight;
          menu.style.top = `${parentRect.top}px`; // Align top of the parent

          // Ensure the menu does not overflow the viewport height
          if (parentRect.top + menuRect.height > viewportHeight - 10) {
            menu.style.top = `${viewportHeight - menuRect.height - 10}px`; // Adjust top to fit within viewport
          }
          if (parentRect.top - menuRect.height < 0) {
            menu.style.top = `${menuRect.height}px`; // Adjust top to fit within viewport
          }
        }
      }
    };

    // Add scroll event listener to the sidebar
    if (threadList) {
      threadList.addEventListener("scroll", adjustMenuPosition);
    }

    adjustMenuPosition();
    window.addEventListener("resize", adjustMenuPosition);
    return () => {
      if (threadList) {
        threadList.removeEventListener("scroll", adjustMenuPosition);
      }
      window.removeEventListener("resize", adjustMenuPosition);
    };
  }, [threadListRef]);

  // The current value of the chat name input.
  const [draftChatName, setDraftChatName] = React.useState(chatName);

  return (
    <div className={styles.chatThread + " " + styles.editable} ref={menuRef}>
      <span className={styles.threadSpan}>
        <FiEdit3 className={styles.editIcon} />
        <input
          value={draftChatName}
          onChange={(e) => setDraftChatName(e.target.value)}
          onKeyDown={(e) => {
            // Call the save handler if the user presses Enter.
            if (e.key === "Enter") {
              e.preventDefault();
              onSave(chatId, draftChatName);
            }

            // Only collects "Escape" on some browsers from within the input :(
            if (e.key === "Escape") {
              e.preventDefault();
              onCancel();
            }
          }}
          autoFocus
          className={"input-reset " + styles.threadNameInput}
          type="text"
        />
      </span>
      <button
        onClick={() => onCancel()}
        className={styles.innerInputButton + " " + styles.cancel}
      >
        <FiXCircle />
      </button>
      <aside className={styles.editMenu} ref={editMenuRef}>
        <button
          onClick={() => onSave(chatId, draftChatName)}
          className="bg-transparent white pa2 br-pill mr1 bn dim pointer"
        >
          <FiSave className={styles.editMenuButtonIcon} /> Save
        </button>
        <button
          onClick={() => onDelete(chatId)}
          className="bg-transparent white pa2 br-pill mr1 bn dim pointer"
        >
          <FiTrash className={styles.editMenuButtonIcon} /> Delete
        </button>
      </aside>
    </div>
  );
}
